/**
 * Public badge-templates gallery hub (Certifier's /badge-templates).
 *
 * A marketing/discovery page listing every named template style (recognition,
 * …) with a composed hero preview, colour swatches, and two funnels: "Edit this
 * template" → the public designer (/designer?template=<id>), and "View template
 * page" → the SEO blog landing page (/blog/<slug>). Data-driven from the badge
 * manifest's styleMeta, so new styles appear here automatically.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LotusLogo } from '../components/LotusLogo';
import { TEMPLATE_STYLE_META } from '../components/badge-designer-utils';
import { idFor, thumbFor } from '../lib/templateThumbs';
import { useAuth } from '../hooks/useAuth';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function BadgeTemplatesGalleryPage() {
  const { t } = useTranslation('common');
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const styles = Object.entries(TEMPLATE_STYLE_META);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Free Badge Templates — Customise & Issue Verifiable Badges | Badges.Ninja';
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute('content') ?? null;
    desc?.setAttribute(
      'content',
      'Browse free, professionally designed badge templates. Customise any template in seconds and issue it as a verifiable Open Badge — no design skills needed.'
    );
    return () => {
      document.title = prevTitle;
      if (prevDesc !== null) desc?.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <LotusLogo className="h-7 w-7" />
          <span className="text-base font-bold text-gray-900">badges.ninja</span>
        </Link>
        <div className="ml-auto flex items-center gap-3">
          <Link to="/designer" className="text-sm font-medium text-gray-600 hover:text-gray-900">
            {t('templates.openDesigner', 'Open designer')}
          </Link>
          {authLoading ? null : isAuthenticated ? (
            <Link to="/dashboard" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
              {t('nav.goToDashboard', 'Go to Dashboard')}
            </Link>
          ) : (
            <Link to="/signup" className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700">
              {t('templates.startFree', 'Start free')}
            </Link>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 sm:py-14">
        <section className="text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            {t('templates.heroTitle', 'Free Badge Templates')}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-gray-600">
            {t(
              'templates.heroSubtitle',
              'Professionally designed, ready to customise. Pick a template, make it yours in seconds, and issue it as a verifiable Open Badge — no design skills needed.'
            )}
          </p>
        </section>

        <div className="mt-12 space-y-10">
          {styles.map(([style, meta]) => (
            <section
              key={style}
              className="grid grid-cols-1 items-center gap-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8 md:grid-cols-2"
            >
              <div className="flex flex-col items-center">
                <img
                  src={thumbFor(style, meta.default)}
                  alt={`${meta.label} badge template`}
                  width={320}
                  height={320}
                  loading="lazy"
                  className="w-full max-w-[300px]"
                />
              </div>

              <div>
                <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-800">
                  {meta.category}
                </span>
                <h2 className="mt-3 text-2xl font-bold text-gray-900">{meta.label}</h2>
                {meta.description && <p className="mt-2 text-gray-600">{meta.description}</p>}

                <div className="mt-4 grid grid-cols-8 gap-1.5">
                  {meta.colors.map((color) => (
                    <Link
                      key={color}
                      to={`/designer?template=${idFor(style, color)}`}
                      title={cap(color)}
                      className="overflow-hidden rounded-md border border-gray-200 transition-all hover:border-primary-400 hover:shadow"
                    >
                      <img src={thumbFor(style, color)} alt={cap(color)} width={48} height={48} loading="lazy" className="w-full" />
                    </Link>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/designer?template=${idFor(style, meta.default)}`}
                    className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700"
                  >
                    {t('templates.editThis', 'Edit this template')}
                  </Link>
                  {meta.slug && (
                    <a
                      href={`/blog/${meta.slug}`}
                      className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t('templates.viewPage', 'View template page')}
                    </a>
                  )}
                </div>
              </div>
            </section>
          ))}
        </div>

        <section className="mt-14 rounded-2xl bg-primary-600 px-6 py-10 text-center text-white sm:px-10">
          <h2 className="text-2xl font-bold">{t('templates.ctaTitle', 'Issue verifiable badges, free')}</h2>
          <p className="mx-auto mt-2 max-w-xl text-primary-100">
            {t('templates.ctaBody', 'Create an account to save your designs and issue Open Badge credentials your recipients can verify and share.')}
          </p>
          <Link
            to="/signup"
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-base font-semibold text-primary-700 hover:bg-primary-50"
          >
            {t('templates.startFree', 'Start free')}
          </Link>
        </section>
      </main>
    </div>
  );
}
